<template>
  <fragment v-if="campaign">
    <div v-if="campaign.IsAutoCreated" class="autocreated">
      {{ $t("CampaignEdit.IsAutoCreated") }}
    </div>
    <div :class="{ disabled: campaign.IsAutoCreated }">
      <div v-if="campaignEditGoogleData.Campaigns.length === 0" class="caption">
        {{ $t("CampaignEdit.Google.NoCampaignsCaption") }}
      </div>
      <div v-if="campaignEditGoogleData.Campaigns.length > 0" class="connected-campaigns">
        <div class="caption">
          {{ $t("CampaignEdit.Google.ConnectedCampaigns") }}
        </div>
        <ul>
          <li v-for="campaign in campaignEditGoogleData.Campaigns" :key="campaign">
            {{ campaign }}
          </li>
        </ul>
      </div>
      <div v-if="campaignEditGoogleData.AdGroups.length > 0" class="connected-adgroups">
        <div class="caption">
          {{ $t("CampaignEdit.Google.ConnectedAdGroups") }}
        </div>
        <ul>
          <li v-for="adGroup in campaignEditGoogleData.AdGroups" :key="adGroup">
            {{ adGroup }}
          </li>
        </ul>
      </div>
      <div v-if="campaignEditGoogleData.Campaigns.length > 0">
        <div class="token-message">{{ checkTokenMessage }}</div>
        <div class="buttons">
          <b-button :label="$t('CampaignEdit.Google.RepairToken')" v-if="!isTokenValid" @click="repairToken" />
          <b-button :label="$t('CampaignEdit.Google.ClearTemplate')" type="is-primary" @click="clearTemplate" />
        </div>
      </div>
      <div v-else>
        <p v-html="$t('CampaignEdit.Google.NoCampaignsText')"></p>
        <b-button :label="$t('CampaignEdit.Google.SetTemplateBtn')" type="is-primary" class="set-template-btn" @click="openGoogle" />
      </div>
      <div v-if="campaignEditGoogleData.Campaigns.length === 0">
        <div class="caption">{{ $t("CampaignEdit.Google.SendMessageCaption") }}</div>
        <div class="channel-edit-group channel-edit-group-fields">
          <div class="field-w">
            <b-field :label="$t('CampaignEdit.Google.SendMessageEmail')">
              <b-input v-model="sendMessageData.email" :placeholder="$t('CampaignEdit.Google.SendMessageEmail')" />
            </b-field>
          </div>
          <div class="field-w">
            <b-field :label="$t('CampaignEdit.Google.SendMessageText')">
              <b-input type="textarea" rows="3" v-model="sendMessageData.message" :placeholder="$t('CampaignEdit.Google.SendMessageText')" />
            </b-field>
          </div>
          <div class="field-w" v-if="!messageSent">
            <b-button :label="$t('Send')" type="is-primary" @click="sendMessage" />
          </div>
        </div>
        <div class="message-sent" v-if="messageSent">{{ $t("CampaignEdit.Google.MessageSent") }}</div>
      </div>
    </div>
  </fragment>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import Fragment from "vue-fragment";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import { Getter } from "vuex-class";
import i18n from "@/i18n";
import { Result } from "@/models/Result";
import { Campaign, CampaignEditViewModel } from "@/models/campaigns/Campaign";
import { CampaignEditGoogleData } from "@/models/campaigns/CampaignEditGoogleData";
import { CampaignEditPageKind } from "@/models/campaigns/CampaignEditPageKind";
import { CheckChannelCampaignsData } from "@/models/campaigns/CheckChannelCampaignsData";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import GoogleApiService from "@/services/api/GoogleApiService";

Vue.use(Fragment.Plugin);
const namespace = "campaignsModule";

@Component({
  components: {
  },
})
export default class TabGoogle extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", { namespace }) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  checkTokenMessage = "";
  isTokenValid = true;
  messageSent = false;
  campaignEditGoogleData: CampaignEditGoogleData = {
    ConnectUrl: "",
    IsAutoCreated: false,
    MsgToProgrammer: "",
    AdGroups: [],
    Campaigns: [],
  };
  sendMessageData: SendMessageData = {
    email: "",
    message: "",
  }

  CampaignEditPageKind = CampaignEditPageKind;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", { immediate: true })
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    this.loadData();
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get pageKind(): CampaignEditPageKind {
    return CampaignService.getCampaignEditPageKind(this.$route.path);
  }

  //#endregion Getters


  //#region Handlers

  clearTemplate() {
    const loader = this.$loading.show();
    GoogleApiService.clearTemplateGoogle(this.campaign.Id).then((result: Result) => {
      if (result.Success) {
        this.loadData();
        HelperService.showMessage("", i18n.t("CampaignEdit.Google.CampaignDeactivated").toString());
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }

  openGoogle() {
    const loader = this.$loading.show();
    GoogleApiService.connectCampaign(this.campaign.Id).then((result: Result) => {
      if (result.Success) {
        const data = result.Data as CheckChannelCampaignsData;
        if (data.campaignCount > 0) {
          this.loadData();
        }
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }

  repairToken() {
    GoogleApiService.repairToken(this.campaign.Id);
  }

  sendMessage() {
    if (!this.sendMessageData.email) {
      HelperService.showError(i18n.t("EmailRequired").toString());
      return;
    }
    const loader = this.$loading.show();
    CampaignsApiService.sendMessage(this.sendMessageData.email, this.sendMessageData.message).then((result: Result) => {
      if (result.Success) {
        this.messageSent = true;
      } else {
        HelperService.showError(result.Message);
      }
    }).finally(() => {
      loader.hide();
    });
  }
  
  //#endregion Handlers


  //#region Private methods

  loadData() {
    const loader = this.$loading.show();
    CampaignsApiService.getCampaignEditGoogleData(this.campaign).then((data: CampaignEditGoogleData) => {
      this.campaignEditGoogleData = data;
      this.sendMessageData.message = data.MsgToProgrammer;
      if (data.Campaigns.length > 0) {
        GoogleApiService.checkToken(this.campaign.Id).then((result: Result) => {
          if (result.Success) {
            this.isTokenValid = result.Data as boolean;
            if (this.isTokenValid) {
              this.checkTokenMessage = i18n.t("CampaignEdit.Google.TokenIsValid").toString();
            } else {
              this.checkTokenMessage = i18n.t("CampaignEdit.Google.TokenIsNotValid").toString();
              this.checkTokenMessage += ` ${result.Message}`;
            }
          } else {
            HelperService.showError(result.Message);
          }
        }).finally(() => {
          loader.hide();
        });
      } else {
        loader.hide();
      }
    });
  }

  //#endregion Private methods
}

interface SendMessageData {
  email: string;
  message: string;
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.autocreated {
  margin-bottom: 1rem;
}

.buttons {
  button {
    margin-block-end: 1rem;
  }
}

.caption {
  font-weight: bold;
  margin-bottom: 1rem;
}

.connected-adgroups, .connected-campaigns {
  ul {
    list-style: disc;
    margin-bottom: 1rem;

    li {
      margin-left: 1.2rem;
    }
  }
}

.disabled {
  opacity: 0.7;
  pointer-events: none;
}

.channel-edit-group {
  margin-bottom: 25px;

  &.channel-edit-group-field {
    max-width: 894px;

    .channel-edit-field {
      margin-bottom: 1rem;
    }

    .label {
      font-size: 17px;
      margin: 0;
      display: inline;
    }
  }

  &.channel-edit-group-fields {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;

    .field-w {
      padding: 0 12px 25px;
      flex-grow: 1;
      width: 100%;
    }
  }
}

.message-sent {
  color: $success;
}

.set-template-btn {
  margin: 1rem 0;
}

.token-message {
  margin-bottom: 1rem;
}
</style>
